import { Button, Modal } from 'antd'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import classNames from 'classnames'
import { useRafLoop } from 'react-use'

import { poppins } from '@/components/common/fonts'
import {
  IconAudioScriptRecord,
  IconAudioScriptStopRecord,
} from '@/components/common/Icons'
import { useHumanReadableDuration } from '@/components/tools/common/hooks/duration'
import { AUDIO_SCRIPT_MAX_DURATION } from '@/constants/tools/solid-background-video'
import { useToolsNotification } from '@/components/tools/common/hooks/notification'

export const RecordModal: FC<{
  isOpened: boolean
  onClose: () => void
  onRecorded: (file: File) => void
}> = ({ isOpened, onClose, onRecorded }) => {
  return (
    <Modal
      open={isOpened}
      onCancel={onClose}
      destroyOnClose
      centered
      footer={null}
      styles={{
        content: {
          padding: 0,
        },
      }}
      closeIcon={null}
      width={600}
    >
      <RecordModalBody onClose={onClose} onRecorded={onRecorded} />
    </Modal>
  )
}

const RecordModalBody: FC<{
  onClose: () => void
  onRecorded: (file: File) => void
}> = ({ onClose, onRecorded }) => {
  const { t } = useTranslation('common')

  const { open: openNotification, contextHolder } = useToolsNotification()

  const mediaStreamRef = useRef<MediaStream>()
  const mediaRecorderRef = useRef<MediaRecorder>()
  const chunksRef = useRef<Blob[]>([])

  const [isRecording, setIsRecording] = useState(false)

  const [recordStartedAt, setRecordStartedAt] = useState<number>()
  const [currentTime, setCurrentTime] = useState(() => Date.now())

  useEffect(() => {
    if (!isRecording) {
      return
    }

    setRecordStartedAt(Date.now())
  }, [isRecording])

  const [stopLoop, startLoop] = useRafLoop(() => {
    setCurrentTime(Date.now())
  }, false)

  useEffect(() => {
    if (isRecording) {
      startLoop()
    } else {
      stopLoop()
    }
  }, [stopLoop, startLoop, isRecording])

  const recordedDuration = useMemo(() => {
    if (!isRecording || !recordStartedAt) {
      return 0
    }

    return Math.max(currentTime - recordStartedAt, 0)
  }, [isRecording, recordStartedAt, currentTime])

  const humanReadableRecordedDuration = useHumanReadableDuration(
    recordedDuration / 1000,
  )

  /**
   * 모달 표시하면 바로 권한 요청 해보기
   */
  useEffect(() => {
    const query = async () => {
      let mediaStream: MediaStream | undefined

      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        })
      } catch (e) {
        console.error('mic permission', e)
        openNotification(
          t('Cannot use audio currently. Please check your audio condition.'),
        )
      } finally {
        if (mediaStream) {
          mediaStream.getTracks().forEach((track) => {
            track.stop()
          })
        }
      }
    }

    query()
  }, [openNotification, t])

  const resetStreamAndRecorder = useCallback(() => {
    mediaRecorderRef.current?.stop()
    mediaRecorderRef.current = undefined

    mediaStreamRef.current?.getTracks().forEach((track) => {
      track.stop()
    })
    mediaStreamRef.current = undefined

    chunksRef.current = []
  }, [])

  const startRecording = useCallback(async () => {
    if (isRecording) {
      return
    }

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      })

      const mediaRecorder = new MediaRecorder(mediaStream)

      /**
       * 녹음중 상태 설정 관련
       */
      mediaRecorder.addEventListener('start', () => {
        setIsRecording(true)
      })
      mediaRecorder.addEventListener('stop', () => {
        setIsRecording(false)
      })

      /**
       * 녹음 데이터 수집
       */
      mediaRecorder.addEventListener('dataavailable', (e) => {
        chunksRef.current.push(e.data)
      })

      /**
       * 에러 관련 처리
       */
      mediaRecorder.addEventListener('error', (e) => {
        console.error('error', e)

        openNotification(
          t('Cannot use audio currently. Please check your audio condition.'),
        )

        resetStreamAndRecorder()
      })

      mediaRecorder.start()

      mediaStreamRef.current = mediaStream
      mediaRecorderRef.current = mediaRecorder
    } catch (e) {
      console.error(e)

      openNotification(
        t('Cannot use audio currently. Please check your audio condition.'),
      )

      resetStreamAndRecorder()
    }
  }, [isRecording, resetStreamAndRecorder, openNotification, t])

  const stopRecording = useCallback(() => {
    if (!isRecording) {
      return
    }

    if (!mediaRecorderRef.current) {
      return
    }

    mediaRecorderRef.current.addEventListener('stop', () => {
      const file = new File(
        chunksRef.current,
        /**
         * 녹음된 파일명은 recorded_audio.mp3로 고정
         */
        'recorded_audio.mp3',
        {
          /**
           * 타입을 지정하지 않으면 사파리에서는 재생이 안 됨\
           * 실제 파일의 타입이 audio/mpeg가 아닐 수 있지만\
           * 기능에 큰 영향을 주는 것이 아니라서 가짜로 이렇게 지정함
           */
          type: 'audio/mpeg',
        },
      )

      onRecorded(file)
      onClose()
    })

    mediaRecorderRef.current.stop()
  }, [isRecording, onClose, onRecorded])

  const handleToggleRecord = () => {
    if (isRecording) {
      stopRecording()
    } else {
      startRecording()
    }
  }

  useEffect(() => {
    if (!isRecording) {
      return
    }

    if (recordedDuration < AUDIO_SCRIPT_MAX_DURATION) {
      return
    }

    stopRecording()
  }, [isRecording, recordedDuration, stopRecording])

  const startStopButtonMessage = useMemo(
    () =>
      isRecording
        ? t('Click the red button to stop recording')
        : t('Click the red button to record your voice!'),
    [t, isRecording],
  )

  useEffect(() => {
    return () => {
      console.log('cleanup')

      resetStreamAndRecorder()
    }
  }, [resetStreamAndRecorder])

  return (
    <RecordModalContainer>
      <Title>{t('Audio Record')}</Title>

      <Content>
        <ContentInner>
          <LikeTooltip>{startStopButtonMessage}</LikeTooltip>

          <RecordToggleButton onClick={handleToggleRecord}>
            {isRecording ? (
              <IconAudioScriptStopRecord />
            ) : (
              <IconAudioScriptRecord />
            )}
          </RecordToggleButton>

          <Timer
            className={classNames({
              visible: isRecording,
            })}
          >
            {humanReadableRecordedDuration}
          </Timer>
        </ContentInner>

        <CancelButton
          onClick={() => {
            setIsRecording(false)
            onClose()
          }}
        >
          {t('Cancel')}
        </CancelButton>
      </Content>

      {contextHolder}
    </RecordModalContainer>
  )
}

const RecordModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  padding: 40px;
`

const Title = styled.div`
  font-family: ${poppins.string};
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
`

const ContentInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;

  max-width: 300px;
  width: 100%;
  padding: 20px 10px;
  box-sizing: border-box;

  border: 1px solid #958e8e;
  border-radius: 10px;
`

const LikeTooltip = styled.div`
  position: relative;

  border-radius: 4px;
  padding: 4px 10px;
  background-color: #000000;

  font-family: ${poppins.string};
  font-size: 11px;
  font-weight: 400;
  line-height: 12.89px;
  color: #ffffff;
  text-align: center;

  z-index: 0;

  &::before {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    background-color: #000000;
    bottom: 0;
    left: 50%;

    z-index: -1;

    transform: translate(-50%, 50%) rotate(45deg);
  }
`

const RecordToggleButton = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;

  width: auto;
  height: auto;
  padding: 0;

  border: 0;
  border-radius: 9999px;
`

/**
 * &.visible
 */
const Timer = styled.div`
  background-color: #000000;
  border-radius: 9999px;
  padding: 4px 24px;

  font-family: ${poppins.string};
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
  letter-spacing: -0.025em;
  color: #ffffff;

  visibility: hidden;

  &.visible {
    visibility: visible;
  }
`

const CancelButton = styled(Button)`
  height: 40px;
  padding-inline: 48px;

  font-family: ${poppins.string};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`
